import { IonImg, IonText } from '@ionic/react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { mergeClassNames } from '@/utils';

import { CountdownImages } from '@/assets/countdown';

import styles from './CalibrationCountdown.module.css';

interface CalibrationCountdownProps {
  isPlaying: boolean;
  /** Duration of the countdown in seconds */
  duration?: number;
  onFinish?: () => void;
}

export const CalibrationCountdown = ({ isPlaying, duration = 3, onFinish }: CalibrationCountdownProps) => {
  const [active, setActive] = useState<number | 'full'>('full');
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const images = useMemo(() => {
    return Array(89)
      .fill(0)
      .map((_, i) => i);
  }, []);

  /**
   * Clears the specified interval if it is not null and resets the interval reference.
   *
   * @param interval - The interval to be cleared, which can be a NodeJS.Timeout or null.
   */
  function nullableClearInterval(interval: NodeJS.Timeout | null) {
    if (interval) {
      clearInterval(interval);
      intervalRef.current = null;
    }
  }

  /**
   * Manages the countdown timer based on the `isPlaying` state.
   *
   * If `isPlaying` is true, sets an interval to decrement the `active` state
   * every calculated milliseconds based on the duration. When `active` reaches 0, the interval is cleared
   * and `active` is set to 'full'. If `active` is 'full', the interval is also cleared.
   *
   * If `isPlaying` is false, clears the interval and any existing timeout.
   */
  useEffect(() => {
    if (isPlaying) {
      const intervalDuration = (duration * 1000) / 89; // Calculate the interval based on the duration in seconds
      intervalRef.current = setInterval(() => {
        setActive((prev) => {
          if (prev === 88) {
            nullableClearInterval(intervalRef.current);
            return 88;
          }

          if (prev === 'full') {
            return 0;
          }

          return prev + 1;
        });
      }, intervalDuration);
    } else {
      nullableClearInterval(intervalRef.current);
    }

    return () => {
      nullableClearInterval(intervalRef.current);
    };
  }, [isPlaying, duration]);

  /**
   * Calculates the remaining time based on the current active state.
   *
   * Uses a memoized function to determine the remaining time by comparing
   * the current active state to a maximum index and a total time. If the
   * active state is 'full', it returns 0. Otherwise, it calculates the
   * remaining time as a proportion of the total time, rounded up to the
   * nearest integer. If the calculated remaining time is 0, it returns '1'.
   */
  const time = useMemo(() => {
    if (active === 'full') {
      return duration;
    }

    const maxIndex = 88;
    const totalTime = duration;

    const elapsedTime = (active / maxIndex) * totalTime;
    const remainingTime = Math.ceil(totalTime - elapsedTime);

    if (remainingTime === 0) {
      onFinish?.();
    }

    return remainingTime;
  }, [active, duration]);

  return (
    <div className={styles.wrapper}>
      {images.map((image) => (
        <IonImg
          className={mergeClassNames(styles.countdown, active === image && styles.active)}
          key={image}
          src={CountdownImages.get(image)}
        />
      ))}

      <IonImg
        className={mergeClassNames(styles.countdown, active === 'full' && styles.active)}
        src={CountdownImages.get('full')}
      />

      <IonText className={styles.time}>{time}</IonText>
    </div>
  );
};
